import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import Sketch from '@arcgis/core/widgets/Sketch';
import * as turf from '@turf/turf';
import { Position } from 'geojson';

import { hexToRgb } from '@lib/common/helpers';
// Removed EsriMap import to avoid circular dependency
import { FsMap } from '@lib/geo/map';

import { FsShapeStyles } from '../helpers';
import { ShapeStylesType } from '../types';
import { FsMapShape } from './shapes';

/**
 * Approximate radius of the Earth in miles. Needed to calculate the polygon coordinates.
 * Source: https://en.wikipedia.org/wiki/Earth_radius
 */
const EARTH_RADIUS_MILES = 3963.1;

export interface CreatePolygonFromPointOptions {
  lng: number;
  lat: number;
  radiusMiles?: number; // Radius of the polygon in miles, Default: 1
  numberOfSides?: number; // Number of sides for the polygon (more sides = more circular). Default: 16
}

type ShapeOptions = {
  editable: boolean;
  draggable: boolean;
};

export class FsMapPolygon extends FsMapShape {
  public shapeType = 'polygon';
  public isEditable: boolean = false;
  public isDraggable: boolean = false;
  public polygonStyle: ShapeStylesType;
  public sketchPolygon: Position[];

  private _esriUndoButton: HTMLElement;
  private _graphicsLayer: GraphicsLayer;
  private _sketch: Sketch;

  /**
   * Create a polygon with a specified number of sides around a point.
   * This is a utility method for generating polygon coordinates.
   *
   * @param options - The options to create the polygon.
   * @returns Array of coordinate arrays representing the polygon
   */
  public static createFromPoint(options: CreatePolygonFromPointOptions): number[][][] {
    const lng = options.lng;
    const lat = options.lat;
    const radiusMiles = options.radiusMiles || 1;
    const numberOfSides = options.numberOfSides || 16;
    const angleStep = (2 * Math.PI) / numberOfSides; // Angle between each side of the polygon.
    const radiusRadians = radiusMiles / EARTH_RADIUS_MILES;

    if (numberOfSides < 3) {
      throw new Error('Number of sides must be at least 3');
    }
    if (radiusMiles < 0) {
      /** If negative radius, return an empty array. */
      return [[]];
    }

    const coordinates: number[][] = [];
    /** Loop through the number of sides and calculate the coordinates for each side. */
    for (let i = 0; i < numberOfSides; i++) {
      const angle = i * angleStep; // Calculate the angle for this side of the polygon.
      const latRadians = lat * (Math.PI / 180); // Convert the latitude to radians.
      const lngRadians = lng * (Math.PI / 180); // Convert the longitude to radians.
      /** Calculate the new latitude and longitude for this side of the polygon. */
      const newLat = Math.asin(
        Math.sin(latRadians) * Math.cos(radiusRadians) +
          Math.cos(latRadians) * Math.sin(radiusRadians) * Math.cos(angle),
      );
      /** Calculate the new longitude for this side of the polygon. */
      const newLng =
        lngRadians +
        Math.atan2(
          Math.sin(angle) * Math.sin(radiusRadians) * Math.cos(latRadians),
          Math.cos(radiusRadians) - Math.sin(latRadians) * Math.sin(newLat),
        );
      /** Add the new coordinates to the list of coordinates. */
      coordinates.push([newLng * (180 / Math.PI), newLat * (180 / Math.PI)]);
    }
    /** Close the polygon by repeating the first coordinate. */
    coordinates.push(coordinates[0]);
    return [coordinates];
  }

  constructor(public shape?: any) {
    super({
      shape,
    });
  }

  public createPolygon(
    paths: any[],
    style: ShapeStylesType,
    map: FsMap,
    options: ShapeOptions,
  ): FsMapPolygon {
    this.map = map;
    this.isEditable = options.editable;
    this.isDraggable = options.draggable;
    this.polygonStyle = style;

    switch (this.defaultMapSource) {
      case 'google':
        const coordinates = paths.map(([lng, lat]) => ({ lat, lng }));
        this.shape = new google.maps.Polygon({
          paths: coordinates,
          ...this.polygonStyle,
          editable: this.isEditable,
          draggable: this.isDraggable,
        });
        this.listenForGoogleShapeEvents();
        return this;
      case 'esri':
        if (options.editable) {
          this.editPolygon(options, style, map);
        } else {
          const rings = paths;
          const styles: any = new FsShapeStyles({ ...style }).styles;
          const simpleFill = new SimpleFillSymbol(styles);
          const polygon = new Polygon({
            spatialReference: {
              wkid: 4326,
            },
            rings,
          });

          this.shape = new Graphic({
            attributes: {
              id: crypto.randomUUID(),
            },
            geometry: polygon,
            symbol: simpleFill,
          });

          this._sketch = new Sketch({
            view: (this.map as any).mapView,
            visibleElements: {
              createTools: {
                point: false,
                polyline: false,
                polygon: false,
                rectangle: false,
                circle: false,
              },
              selectionTools: {
                'rectangle-selection': false,
                'lasso-selection': false,
              },
              settingsMenu: false,
            },
            layer: this._graphicsLayer,
            creationMode: 'update',
          });

          this.setupEsriClickHandler();

          return this;
        }
    }
  }

  public editPolygon(options: ShapeOptions, style: ShapeStylesType, map: FsMap): FsMapPolygon {
    this.map = map;
    switch (this.defaultMapSource) {
      case 'google':
        this.polygonStyle = style;
        this.shape.setOptions({
          ...style,
          editable: options.editable,
          draggable: options.draggable,
          strokeWeight: style.strokeWidth,
        });
        break;
      case 'esri':
        this._graphicsLayer = new GraphicsLayer();
        (this.map as any).mapView.map.add(this._graphicsLayer);

        const esriPolygon = new Polygon({
          rings: this.shape.geometry.rings,
          spatialReference: { wkid: 4326 }, // WGS84
        });

        const graphic = new Graphic({
          geometry: esriPolygon,
          symbol: new SimpleFillSymbol({
            color: hexToRgb(style.fillColor, style.fillOpacity),
            style: 'solid',
            outline: {
              color: style.strokeColor,
              width: style.strokeWidth,
            },
          }),
        });
        this._graphicsLayer.add(graphic);

        this._sketch = new Sketch({
          view: (this.map as any).mapView,
          layer: this._graphicsLayer,
          creationMode: 'update',
        });

        this._sketch.update([graphic], { tool: 'reshape' });
        this.map.removeShape(this.shape);
        this._sketch.on('update', (event) => {
          if (event.graphics.length > 0) {
            this.sketchPolygon = (event.graphics[0].geometry as Polygon)?.rings[0] as Position[];
          }
          if (event.toolEventInfo && event.toolEventInfo.type === 'move-stop') {
            if (this._esriUndoButton) this._esriUndoButton.remove();
          }

          if (event.toolEventInfo && event.toolEventInfo.type === 'reshape-stop') {
            const toolEventInfo = event.toolEventInfo as unknown as {
              mover: {
                geometry: {
                  latitude: number;
                  longitude: number;
                  spatialReference: { wkid: number };
                };
                attributes: {
                  pathIndex: number;
                  pointIndex: number;
                };
              };
            };
            const movedVertex = toolEventInfo.mover.geometry;

            if (movedVertex) {
              const movedPoint = new Point({
                latitude: movedVertex.latitude,
                longitude: movedVertex.longitude,
                spatialReference: { wkid: 4326 },
              });

              const screenPoint = (this.map as any).mapView.toScreen(movedPoint);

              if (this._esriUndoButton) {
                this._esriUndoButton.remove();
              }

              const undoButton = document.createElement('button');
              undoButton.setAttribute('aria-label', 'Undo Reshape');
              undoButton.innerHTML = `<i class="material-icons">undo</i>`;
              undoButton.style.position = 'absolute';
              undoButton.style.left = `${screenPoint.x}px`;

              document.body.appendChild(undoButton);
              const buttonHeight = undoButton.offsetHeight;
              undoButton.style.top = `${screenPoint.y - buttonHeight}px`;

              undoButton.onclick = () => {
                this._sketch.undo();
                undoButton.remove();
              };

              this._esriUndoButton = undoButton;
            }
          }
        });
    }
    return this;
  }

  public getGeometry(): any {
    const path: any[] = this.getPath();
    path.push(path[0]);
    return turf.geometry('Polygon', [path]);
  }
}
